import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useUser } from './useUser';
import { TApiUserLocation } from '../types/User';
import { IAppState } from '../store/Store';

const STORAGE_KEY = 'chemicalSelectedLocationId';

const readStored = (): number | null => {
  const raw = localStorage.getItem(STORAGE_KEY);
  if (raw === null || raw === '') {
    return null;
  }
  const parsed = parseInt(raw, 10);
  return Number.isFinite(parsed) ? parsed : null;
};

const findLocationIdByTitle = (
  locations: Array<TApiUserLocation>,
  title: string | undefined
): number | null => {
  if (!title) {
    return null;
  }
  const lowered = title.toLocaleLowerCase();
  return locations.find((l) => l.title.toLocaleLowerCase() === lowered)?.id ?? null;
};

export const useSelectedLocation = () => {
  const { getUserLocations } = useUser();
  const userLocations = getUserLocations();
  const searchLocation = useSelector<IAppState, string | undefined>(
    (state) => state.chemicalState.searchLocation
  );

  const filterLocationId = findLocationIdByTitle(userLocations, searchLocation);
  const defaultLocationId = userLocations.length > 0 ? userLocations[0].id : null;
  const storedAccessibleId = (() => {
    const stored = readStored();
    if (stored === null) {
      return null;
    }
    return userLocations.some((l) => l.id === stored) ? stored : null;
  })();

  const [selectedLocationId, setSelectedLocationIdState] = useState<number | null>(
    () => filterLocationId ?? storedAccessibleId ?? defaultLocationId
  );

  useEffect(() => {
    const accessible =
      selectedLocationId !== null &&
      userLocations.some((l) => l.id === selectedLocationId);
    if (accessible) {
      return;
    }
    const next = filterLocationId ?? storedAccessibleId ?? defaultLocationId;
    if (next !== selectedLocationId) {
      setSelectedLocationIdState(next);
    }
  }, [
    filterLocationId,
    storedAccessibleId,
    defaultLocationId,
    selectedLocationId,
    userLocations,
  ]);

  const setSelectedLocationId = useCallback((id: number | null) => {
    if (id === null) {
      localStorage.removeItem(STORAGE_KEY);
    } else {
      localStorage.setItem(STORAGE_KEY, String(id));
    }
    setSelectedLocationIdState(id);
  }, []);

  const selectedLocation: TApiUserLocation | null =
    userLocations.find((l) => l.id === selectedLocationId) ?? null;

  return {
    selectedLocationId,
    selectedLocation,
    setSelectedLocationId,
    userLocations,
  };
};
